import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type MqlReportResult, type MqlSettingsResult } from '@whinerp/client';
import { formatCount } from '@whinerp/contracts/format';
import { Button, CapabilityUnavailable, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * The MQL definition, and what sales said about it (F-078).
 *
 * Settings and the rejection report on one panel, because "this screen is
 * where the alignment argument is settled": the threshold is changed looking
 * at what sales accepted and turned back at each score, not from memory.
 */
export function MqlSettingsPanel(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-078');
  const { can } = useSession();
  const manage = can('segment.manage');
  const [threshold, setThreshold] = useState('');
  const [hours, setHours] = useState('');
  const [newReason, setNewReason] = useState('');

  const settings = useQuery<MqlSettingsResult, ApiError>({
    queryKey: ['mql-settings'],
    queryFn: () => api.mqlSettings({}),
    enabled: enabled.enabled,
  });
  const report = useQuery<MqlReportResult, ApiError>({
    queryKey: ['mql-report'],
    queryFn: () => api.mqlReport({ query: { days: 90 } }),
    enabled: enabled.enabled && can('analytics.view'),
  });
  useEffect(() => {
    if (!settings.data) return;
    setThreshold(settings.data.threshold === null ? '' : String(settings.data.threshold));
    setHours(String(settings.data.slaHours));
  }, [settings.data]);

  const save = useMutation<unknown, ApiError, string[] | undefined>({
    mutationFn: (retire) =>
      api.saveMqlSettings({
        body: {
          threshold: threshold.trim() === '' ? null : Number(threshold),
          slaHours: Number(hours),
          newReason: newReason.trim() || null,
          ...(retire ? { retireReasonIds: retire } : {}),
        },
      }),
    onSuccess: async () => {
      setNewReason('');
      await queryClient.invalidateQueries({ queryKey: ['mql-settings'] });
    },
  });

  const heading = (
    <div className="flex flex-col gap-1">
      <SectionLabel>{t('mql.title')}</SectionLabel>
      <p className="text-meta text-ink-meta">{t('mql.body')}</p>
    </div>
  );
  if (enabled.state === 'off' || enabled.state === 'plan-locked') {
    return (
      <Panel data-region="mql-settings" id="mql">
        <div className="flex flex-col gap-3">
          {heading}
          <CapabilityUnavailable
            kind={enabled.state === 'off' ? 'module-off' : 'plan-locked'}
            capability={t('mql.title')}
            title={t('capability.moduleOff.title')}
            body={t('capability.moduleOff.body', { values: { capability: t('mql.title') } })}
            action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
          />
        </div>
      </Panel>
    );
  }
  if (settings.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (settings.isError) {
    return (
      <Panel data-region="mql-settings" id="mql">
        <p className="text-dense text-danger-text">{errorText(settings.error)}</p>
      </Panel>
    );
  }

  const input = 'h-input w-col-count rounded-control border border-control bg-surface px-3 text-body text-ink';
  return (
    <Panel data-region="mql-settings" id="mql">
      <div className="flex flex-col gap-3">
        {heading}
        {save.error ? <p data-mql-settings-refusal className="text-dense text-danger-text">{errorText(save.error)}</p> : null}
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('mql.thresholdLabel')}
            <input data-mql-threshold-input type="number" className={input} value={threshold} disabled={!manage} onChange={(event) => setThreshold(event.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('mql.hoursLabel')}
            <input data-mql-hours-input type="number" className={input} value={hours} disabled={!manage} onChange={(event) => setHours(event.target.value)} />
          </label>
          {manage ? (
            <Button intent="secondary" size="small" data-mql-save disabled={save.isPending} onClick={() => save.mutate(undefined)}>
              {t('mql.save')}
            </Button>
          ) : null}
        </div>
        <p className="text-meta text-ink-meta">{t('mql.thresholdHelp')}</p>

        <h3 className="text-dense font-medium text-ink">{t('mql.reasons')}</h3>
        <ul className="flex flex-col">
          {settings.data.reasons.filter((reason) => reason.active).map((reason) => (
            <li key={reason.reasonId} data-mql-reason-row={reason.reasonId} className="flex items-center gap-2 text-dense text-ink">
              <span className="min-w-0 flex-1">{reason.label}</span>
              {manage ? (
                <Button intent="ghost" size="small" data-mql-retire={reason.reasonId} onClick={() => save.mutate([reason.reasonId])}>
                  {t('mql.retire')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
        {manage ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex min-w-0 flex-1 flex-col gap-1 text-dense text-ink-600">
              {t('mql.newReason')}
              <input data-mql-new-reason className="h-input rounded-control border border-control bg-surface px-3 text-body text-ink" value={newReason} onChange={(event) => setNewReason(event.target.value)} />
            </label>
            <Button intent="secondary" size="small" data-mql-add-reason disabled={save.isPending || newReason.trim() === ''} onClick={() => save.mutate(undefined)}>
              {t('mql.addReason')}
            </Button>
          </div>
        ) : null}

        {report.data ? (
          <section data-mql-report className="flex flex-col gap-2">
            <h3 className="text-dense font-medium text-ink">{t('mql.reportTitle')}</h3>
            <p className="text-meta text-ink-meta">
              {t('mql.reportSummary', {
                values: {
                  decided: formatCount(report.data.decided, format),
                  accepted: formatCount(report.data.accepted, format),
                  rejected: formatCount(report.data.rejected, format),
                },
              })}
            </p>
            <ul className="flex flex-col">
              {report.data.byReason.map((reason) => (
                <li key={reason.reasonId} data-mql-report-reason={reason.label} className="flex justify-between text-dense text-ink">
                  <span>{reason.label}</span>
                  <span>{formatCount(reason.count, format)}</span>
                </li>
              ))}
            </ul>
            {report.data.byScore.length > 0 ? (
              <ul className="flex flex-col">
                {report.data.byScore.map((band) => (
                  <li key={band.from} data-mql-band={band.from} className="text-meta text-ink-600">
                    {t('mql.band', {
                      values: {
                        from: String(band.from),
                        to: String(band.to),
                        accepted: formatCount(band.accepted, format),
                        rejected: formatCount(band.rejected, format),
                      },
                    })}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}
      </div>
    </Panel>
  );
}
